/**
 * Checkout intent: "I clicked Get Pro (yearly) before I had an account".
 *
 * An anonymous visitor who picks a paid plan while billing is live goes to
 * sign-up with a `redirect_url` back to the pricing page carrying the intent
 * (`?intent=checkout&plan=pro&interval=year`). Clerk honours `redirect_url`,
 * and the intent is ALSO kept in sessionStorage for flows that drop query
 * params (OAuth round trips, email-code verification). Back on pricing, a
 * signed-in visitor with a fresh intent goes straight into Stripe Checkout.
 *
 * Pure helpers (URL building / parsing / freshness) are unit tested; storage
 * access is guarded so SSR and privacy modes never throw.
 */
import { localizedPath } from './i18n/routes'
import type { Locale } from './i18n/config'
import { BILLING_INTERVALS, PAID_PLAN_IDS } from './pricing'
import type { BillingInterval, PaidPlanId } from './pricing'

export type CheckoutIntent = {
  plan: PaidPlanId
  interval: BillingInterval
  /** Team seats (5 included); omitted for Pro. */
  seats?: number
}

/** An intent older than this is ignored (the visitor moved on). */
export const INTENT_TTL_MS = 30 * 60 * 1000
const STORAGE_KEY = 'as_checkout_intent'

function isPlan(value: unknown): value is PaidPlanId {
  return (PAID_PLAN_IDS as readonly unknown[]).includes(value)
}

function isInterval(value: unknown): value is BillingInterval {
  return (BILLING_INTERVALS as readonly unknown[]).includes(value)
}

function parseSeats(raw: string | null | undefined): number | undefined {
  if (!raw) return undefined
  const seats = Number(raw)
  return Number.isInteger(seats) && seats > 0 && seats <= 1000
    ? seats
    : undefined
}

/** Query string fragment for an intent (no leading `?`). */
export function intentSearchParams(intent: CheckoutIntent): string {
  const params = new URLSearchParams({
    intent: 'checkout',
    plan: intent.plan,
    interval: intent.interval,
  })
  if (intent.plan === 'team' && intent.seats) {
    params.set('seats', String(intent.seats))
  }
  return params.toString()
}

/** Pricing page URL that resumes the checkout for `intent`. */
export function pricingIntentPath(locale: Locale, intent: CheckoutIntent) {
  return `${localizedPath('pricing', locale)}?${intentSearchParams(intent)}`
}

/** Sign-up URL that returns to the pricing page and resumes the checkout. */
export function signUpForCheckoutPath(locale: Locale, intent: CheckoutIntent) {
  const back = pricingIntentPath(locale, intent)
  return `${localizedPath('register', locale)}?redirect_url=${encodeURIComponent(back)}`
}

/** Read an intent from a location search string, or null. */
export function parseIntentSearch(search: string): CheckoutIntent | null {
  const params = new URLSearchParams(search)
  if (params.get('intent') !== 'checkout') return null
  const plan = params.get('plan')
  const interval = params.get('interval')
  if (!isPlan(plan) || !isInterval(interval)) return null
  const seats = plan === 'team' ? parseSeats(params.get('seats')) : undefined
  return seats ? { plan, interval, seats } : { plan, interval }
}

type StoredIntent = CheckoutIntent & { savedAt: number }

export function parseStoredIntent(
  raw: string | null,
  now: number,
): CheckoutIntent | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<StoredIntent>
    if (!isPlan(value.plan) || !isInterval(value.interval)) return null
    if (typeof value.savedAt !== 'number' || now - value.savedAt > INTENT_TTL_MS) {
      return null
    }
    const seats =
      value.plan === 'team' && typeof value.seats === 'number'
        ? parseSeats(String(value.seats))
        : undefined
    return seats
      ? { plan: value.plan, interval: value.interval, seats }
      : { plan: value.plan, interval: value.interval }
  } catch {
    return null
  }
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function saveCheckoutIntent(intent: CheckoutIntent) {
  try {
    storage()?.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...intent, savedAt: Date.now() } satisfies StoredIntent),
    )
  } catch {
    // Storage full or blocked: the URL param still carries the intent.
  }
}

/** Read (and consume) the pending intent: URL first, then storage. */
export function takeCheckoutIntent(search: string): CheckoutIntent | null {
  const fromUrl = parseIntentSearch(search)
  const store = storage()
  let fromStorage: CheckoutIntent | null = null
  try {
    fromStorage = parseStoredIntent(store?.getItem(STORAGE_KEY) ?? null, Date.now())
    store?.removeItem(STORAGE_KEY)
  } catch {
    fromStorage = null
  }
  return fromUrl ?? fromStorage
}
